import { createSlice } from "@reduxjs/toolkit";
import { snakeToCamel, sentenceToCamel } from "../../utils/transformString";

const initialState = {
  heart: {
    bloodPressure: {
      id: "1",
      classId: 2,
      title: "Blood Pressure",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "milimeterMercury",
      unit: {
        milimeterMercury: {
          name: "milimeterMercury",
          symbol: "mmHg",
          to: (value, chart) => {
            if (typeof value !== "object") return;
            if (chart)
              return {
                systolic: value.systolic.toFixed(1),
                diastolic: value.diastolic.toFixed(1),
              };
            return `${value.systolic}/${value.diastolic}`;
          },
          from: (value) => {
            return {
              systolic: parseFloat(value.systolic),
              diastolic: parseFloat(value.diastolic),
            };
          },
          formConfig: [
            {
              symbol: "mmHg",
              name: "systolic",
              type: "number",
              min: 40,
              max: 300,
              step: "1",
            },
            {
              symbol: "mmHg",
              name: "diastolic",
              type: "number",
              min: 30,
              max: 200,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: [
          {
            label: "Diastolic",
            path: "diastolic",
            backgroundColor: "#60a5faee",
          },
          {
            label: "Systolic",
            path: "systolic",
            backgroundColor: "#f43f5edd",
            borderRadius: 4.5,
          },
        ],
        multiValue: true,
      },
    },
    heartRate: {
      id: "9",
      classId: 2,
      title: "Heart Rate",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "beatsPerMinute",
      unit: {
        beatsPerMinute: {
          name: "beatsPerMinute",
          symbol: "bpm",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "bpm",
              name: "value",
              type: "number",
              min: 30,
              max: 350,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Heart Rate",
        },
        multiValue: false,
      },
    },
    heartRateVariability: {
      id: "10",
      classId: 2,
      title: "Heart Rate Variability",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "milisecond",
      unit: {
        milisecond: {
          name: "milisecond",
          symbol: "ms",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "ms",
              name: "value",
              type: "number",
              min: 5,
              max: 350,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Heart Rate",
        },
        multiValue: false,
      },
    },
    cardioFitness: {
      id: "11",
      classId: 2,
      title: "Cardio Fitness",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "maxOxygenVolume",
      unit: {
        maxOxygenVolume: {
          name: "maxOxygenVolume",
          symbol: `VO2max`,
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "VO2max",
              name: "value",
              type: "number",
              min: 1,
              max: 100,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Cardio Fitness",
        },
        multiValue: false,
      },
    },
    peripheralPerfusionIndex: {
      id: "12",
      classId: 2,
      title: "Peripheral Perfusion Index",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "percentage",
      unit: {
        percentage: {
          name: "percentage",
          symbol: "%",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "%",
              name: "value",
              type: "number",
              min: 0,
              max: 25,
              step: "0.01",
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Peripheral Perfusion Index",
        },
        multiValue: false,
      },
    },
    restingHeartRate: {
      id: "13",
      classId: 2,
      title: "Resting Heart Rate",
      type: "heart",
      data: [],
      favourite: false,
      selectedUnit: "beatsPerMinute",
      unit: {
        beatsPerMinute: {
          name: "beatsPerMinute",
          symbol: "bpm",
          to: (value) => parseFloat(value?.toFixed(1)),
          from: (value) => parseFloat(value?.value),
          formConfig: [
            {
              symbol: "bpm",
              name: "value",
              type: "number",
              min: 10,
              max: 300,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Resting Heart Rate",
        },
        multiValue: false,
      },
    },
    walkingHeartRate: {
      id: "14",
      classId: 2,
      title: "Walking Heart Rate",
      type: "heart",
      data: [],
      selectedUnit: "beatsPerMinute",
      unit: {
        beatsPerMinute: {
          name: "beatsPerMinute",
          symbol: "bpm",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "bpm",
              name: "value",
              type: "number",
              min: 30,
              max: 400,
              step: "1",
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Walking Heart Rate",
        },
        multiValue: false,
      },
    },
  },

  componentState: {
    dataState: null,
    vitalsDataState: null,
    firstRun: true,
    firstClick: false,
  },
};

const heartSlice = createSlice({
  name: "heart",
  initialState,
  reducers: {
    addData(state, action) {
      const { responseItem, titleId } = action.payload;
      const key = Object.keys(state.heart).find(
        (key) => state.heart[key].id === titleId
      );

      state.heart[key].data.push({
        id: responseItem?.id,
        date: responseItem?.date,
        time: responseItem?.time,
        value: responseItem?.data.value,
      });
    },

    removeData(state, action) {
      const { id, titleId } = action.payload;
      const dataKey = Object.keys(state.heart).find(
        (key) => state.heart[key].id === titleId
      );

      state.heart[dataKey].data = state.heart[dataKey].data.filter(
        (item) => item.id !== id
      );
    },

    changeUnit(state, action) {
      const { unit, titleId } = action.payload;
      const dataKey = Object.keys(state.heart).find(
        (key) => state.heart[key].id === titleId
      );

      state.heart[dataKey].selectedUnit = unit;
    },

    toggleFavourite(state, action) {
      const { id } = action.payload;
      const dataKey = Object.keys(state.heart).find(
        (key) => state.heart[key].id === id
      );

      state.heart[dataKey].favourite = !state.heart[dataKey].favourite;
    },

    populateData(state, action) {
      const data = action.payload.data;
      const selectedUnit = action.payload.selectedUnit;
      const favourites = action.payload.favourites;
      const titles = action.payload.titles;

      for (let key in data) {
        const array = data[key].map((item) => {
          return {
            id: item.id,
            value: item.data.value,
            date: item.date,
            time: item.time,
          };
        });

        if (state.heart[key]) {
          state.heart[key].data = array;
        }
      }

      for (let key in selectedUnit) {
        const camelKey = snakeToCamel(key);

        if (state.heart[camelKey]) {
          state.heart[camelKey].selectedUnit = snakeToCamel(selectedUnit[key]);
        }
      }

      for (let key in favourites) {
        // const camelKey = snakeToCamel(key);
        if (state.heart[key]) {
          state.heart[key].favourite = favourites[key];
        }
      }

      titles.forEach((title) => {
        const camelName = sentenceToCamel(title.name);
        if (state.heart[camelName]) {
          state.heart[camelName].id = title.id;
          state.heart[camelName].classId = title.class_title_id;
        }
      });
    },

    updateFirstRunState(state, action) {
      state.componentState.firstRun = action.payload;
    },
    updateDataState(state, action) {
      state.componentState.dataState = action.payload;
    },
    updateFirstClick(state, action) {
      state.componentState.firstClick = action.payload;
    },
    updateVitalsDataState(state, action) {
      state.componentState.vitalsDataState = action.payload;
    },
  },
});

export const heartActions = heartSlice.actions;

export default heartSlice.reducer;
